// Helpers over the `GET /api/v1/classes` payload (ClassService.toMap): `date` is the FIRST session start,
// `schedule` lists every session {start,end,location}, `studentCount` is ENROLLED registrations,
// `capacity` is total seats, `lifecycleStatus` is ACTIVE|COMPLETED, `publishStatus` is DRAFT|PUBLISHED.
export const DAY = 86400000;
export const WEEK_HOURS_CAP = 40;

export const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
export const enrolled = (c) => num(c.studentCount ?? c.enrollmentCount);
export const seats = (c) => num(c.capacity ?? c.maxStudents);
export const classDate = (c) => (c.date ? new Date(c.date) : null);
export const scheduleOf = (c) => (Array.isArray(c.schedule) ? c.schedule : []);

/** Routes the dashboards link to (all registered in App.js). */
export const ROUTES = {
  catalog: "/classes",
  myClasses: "/transcript",
  manageCourses: "/course-management",
  approvals: "/coordinator/approvals",
  instructorCourses: "/instructor/courses",
  instructorScheduling: "/instructor/scheduling",
  instructors: "/instructor-management",
  certifications: "/certifications",
  messaging: "/messaging",
  courseHub: (id) => `/course-hub/${id}`,
  studentHub: (id) => `/student-hub/${id}`,
};

export function lastEnd(c) {
  const ends = scheduleOf(c).map((s) => new Date(s.end)).filter((d) => !Number.isNaN(+d));
  return ends.length ? new Date(Math.max(...ends)) : classDate(c);
}

/** A live cohort: published, not completed, and its last session has not finished yet. */
export function isActive(c, now) {
  if (c.lifecycleStatus === "COMPLETED" || c.publishStatus === "DRAFT") return false;
  const end = lastEnd(c);
  return Boolean(end) && end >= now;
}

/** Share of scheduled sessions already finished (0-100), or null with no schedule. */
export function scheduleProgress(c, now) {
  const sessions = scheduleOf(c);
  if (!sessions.length) return null;
  const done = sessions.filter((s) => new Date(s.end) <= now).length;
  return { done, total: sessions.length, pct: Math.round(done / sessions.length * 100) };
}

/**
 * Cohort readiness (the payload has no learner-progress field):
 * behind = starting/started with no instructor, or starting within 7 days under a quarter full;
 * risk   = starting within 30 days under half full, or no instructor yet;
 * ok     = otherwise.
 */
export function readiness(c, now) {
  const start = classDate(c);
  const hasInstructor = Boolean(c.instructorId || c.leadInstructorId);
  const cap = seats(c);
  const fill = cap > 0 ? enrolled(c) / cap : 1;
  const daysToStart = start ? (start - now) / DAY : 0;
  if (!hasInstructor && daysToStart <= 7) return "behind";
  if (daysToStart > 0 && daysToStart <= 7 && fill < 0.25) return "behind";
  if (!hasInstructor || (daysToStart > 0 && daysToStart <= 30 && fill < 0.5)) return "risk";
  return "ok";
}

export function weekBounds(now) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(+d - ((d.getDay() + 6) % 7) * DAY);
  return { start, end: new Date(+start + 7 * DAY) };
}

/** Scheduled hours per instructor id for sessions that start inside the current Mon-Sun week. */
export function weeklyHoursByInstructor(classes, now) {
  const { start, end } = weekBounds(now);
  const hours = {};
  classes.forEach((c) => {
    const owner = c.instructorId || c.leadInstructorId;
    if (!owner) return;
    scheduleOf(c).forEach((s) => {
      const a = new Date(s.start);
      const b = new Date(s.end);
      if (a >= start && a < end && b > a) hours[owner] = (hours[owner] || 0) + (b - a) / 3600000;
    });
  });
  return hours;
}

/** Colour-coded category from the class/cert text (category field first, then name). */
export function categoryKeyFor(...parts) {
  const t = parts.filter(Boolean).join(" ").toLowerCase();
  if (/fire|hazmat|ff\b/.test(t)) return "fire";
  if (/ems|emt|paramedic/.test(t)) return "ems";
  if (/aha|bls|acls|pals|cpr|first aid|heartsaver/.test(t)) return "aha";
  return "res";
}
